import * as React from 'react';
import { useIntl } from 'react-intl';
import { JRCModal } from '../JRCModal/templates/JRCModal';
import { JRCModalProps } from '../JRCModal/templates/JRCModal.types';
import ReactCrop, { centerCrop, Crop, makeAspectCrop } from 'react-image-crop';
import styled, { useTheme } from 'styled-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import 'react-image-crop/dist/ReactCrop.css';
import { JRCIconButton } from '../JRCIconButton/JRCIconButton';
import { JRCFlexBox } from '../JRCFlex/JRCFlexBox';
import { JRCButton } from '../JRCButton/JRCButton';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import { JRCLoader } from '../JRCLoader/JRCLoader';

const ActionWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 8px 0;
`;

const RatioButton = styled.button<{ active?: boolean }>`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s;
    background-color: ${({ theme }) => theme.color.grey0};
    border: 2px solid ${({ active, theme }) => (active ? theme.color.primary : theme.color.grey0)};

    :hover,
    :focus-visible {
        background-color: ${({ theme }) => theme.color.grey1};
    }
`;

const SquareRatioLabel = styled.p`
    height: 20px;
    font-size: 10px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed ${({ theme }) => theme.color.grey4};
    color: ${({ theme }) => theme.color.grey4};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    padding-top: 1px;
`;

const ScreenRatioLabel = styled.p`
    height: 20px;
    font-size: 10px;
    width: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed ${({ theme }) => theme.color.grey4};
    color: ${({ theme }) => theme.color.grey4};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    padding-top: 1px;
`;

const IconWrapper = styled.div`
    height: 20px;
    font-size: 10px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed ${({ theme }) => theme.color.grey4};
    padding-top: 2px;
    padding-left: 1px;
`;

const Separator = styled.div`
    height: 30px;
    width: 3px;
    background-color: ${({ theme }) => theme.color.grey0};
`;

const CropWrapper = styled.div<{
    aspect: number;
    containerAspect: number;
    naturalSize: { height?: number; width?: number };
    isInitialAspect: boolean;
}>`
    display: flex;
    justify-content: center;
    margin-bottom: 8px;
    background-color: ${({ theme }) => theme.color.grey0};

    // Redefinition over theme-reset

    * {
        mask-image: revert-layer;
    }

    svg * {
        pointer-events: none;
    }

    // Handle rotation

    .ReactCrop__child-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        max-height: 400px;
        max-width: 100%;
        aspect-ratio: ${({ aspect }) => aspect};
        ${({ aspect, containerAspect, naturalSize, isInitialAspect }) =>
            aspect < containerAspect
                ? `height: ${
                      naturalSize.height &&
                      naturalSize.width &&
                      (isInitialAspect ? naturalSize.height : naturalSize.width) < 400
                          ? naturalSize.height
                          : 400
                  }px`
                : `width: min(${
                      (isInitialAspect ? naturalSize.width : naturalSize.height) + 'px' ?? 99999
                  }, calc(85vw - 24px))`};
    }
`;

const ImageCrop = styled.img<{ rotation: Rotation; scale: number }>`
    transform: rotate(${({ rotation }) => rotation}deg) scale(${({ scale }) => scale});
`;

export type Area = {
    width: number;
    height: number;
    x: number;
    y: number;
};

type Rotation = 0 | 90 | 180 | 270;
const ROTATIONS: Rotation[] = [0, 90, 180, 270];

export type JRCCropImageProps = Pick<JRCModalProps, 'open' | 'closeHandler'> & {
    aspectRatio?: number | undefined;
    allowCustomRatio?: boolean;
    src?: string | undefined;
    save: (cropConfig: Area & { rotation: number }) => void;
    zIndex?: number;
    loading?: boolean;
};

export function JRCCropImage({ save, ...props }: JRCCropImageProps) {
    const intl = useIntl();
    const theme = useTheme();
    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [imageAspect, setImageAspect] = useState(1);
    const [containerAspect, setContainerAspect] = useState(1);
    const [rotation, setRotation] = React.useState<Rotation>(0);
    const [scale, setScale] = useState(1);
    const [isLockedRatio, setLockedRatio] = useState(!props.allowCustomRatio);
    const [crop, setCrop] = useState<Crop>();
    const [triggerUpdatePosition, setTriggerUpdatePosition] = useState(false);
    const [loaded, isLoaded] = React.useState<boolean>(false);

    const canSave = crop && crop.width > 0 && crop.height > 0;
    const isInitialAspect = rotation === 0 || rotation === 180;
    const cropAspect = isLockedRatio && crop ? crop.width / crop.height : undefined;
    const applyRatio = useCallback(
        (ratio: number) => {
            if (imageRef.current) {
                const { width, height } = imageRef.current;
                const containerWidthToApply = isInitialAspect ? width : height * scale;
                const containerHeightToApply = isInitialAspect ? height : width * scale;
                setCrop(
                    centerCrop(
                        makeAspectCrop(
                            {
                                width: containerWidthToApply,
                                height: containerHeightToApply,
                                unit: 'px',
                            },
                            ratio,
                            containerWidthToApply,
                            containerHeightToApply,
                        ),
                        containerWidthToApply,
                        containerHeightToApply,
                    ),
                );
            }
        },
        [isInitialAspect, scale],
    );

    const initCrop = useCallback(() => {
        applyRatio(cropAspect ?? props.aspectRatio ?? imageAspect);
    }, [applyRatio, cropAspect, imageAspect, props.aspectRatio]);

    const saveCrop = useCallback(() => {
        if (canSave && imageRef.current) {
            const { width, naturalWidth } = imageRef.current;
            const displayResizeFactor = naturalWidth / width / scale;
            save({
                x: Math.round(crop.x * displayResizeFactor),
                y: Math.round(crop.y * displayResizeFactor),
                width: Math.round(crop.width * displayResizeFactor),
                height: Math.round(crop.height * displayResizeFactor),
                rotation,
            });
        }
    }, [canSave, crop, save, rotation, scale]);

    const rotate = useCallback(
        (r: 'left' | 'right') => {
            const i = ROTATIONS.indexOf(rotation);
            let newRotation: Rotation;
            if (r === 'left') {
                newRotation = i === 0 ? ROTATIONS[ROTATIONS.length - 1] : ROTATIONS[i - 1];
            } else {
                newRotation = i === ROTATIONS.length - 1 ? ROTATIONS[0] : ROTATIONS[i + 1];
            }
            setRotation(newRotation);
        },
        [rotation],
    );

    React.useEffect(() => {
        if (props.src) {
            const img = new Image();
            img.src = props.src;
            img.onload = () => {
                isLoaded(true);
                initCrop();
            };
            img.onerror = () => {
                isLoaded(false);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.src]);

    useEffect(() => {
        if (loaded) {
            if (imageRef.current) {
                setImageAspect(
                    isInitialAspect
                        ? imageRef.current.width / imageRef.current.height
                        : imageRef.current.height / imageRef.current.width,
                );
            }
            if (containerRef.current) {
                setContainerAspect(containerRef.current.clientWidth / containerRef.current.clientHeight);
            }
        }
    }, [isInitialAspect, loaded]);

    useEffect(() => {
        if (loaded) {
            if (imageRef.current && containerRef.current) {
                const { height, width } = imageRef.current;
                if (isInitialAspect) {
                    setScale(1);
                } else if (width > height) {
                    setScale(Math.max(width / height, imageRef.current.height / width));
                } else {
                    setScale(Math.min(containerRef.current.clientWidth / height, height / width));
                }
            }
        }
    }, [isInitialAspect, loaded]);

    useEffect(() => {
        setTriggerUpdatePosition(true);
    }, [scale]);

    useEffect(() => {
        if (triggerUpdatePosition) {
            setTriggerUpdatePosition(false);
            initCrop();
        }
    }, [cropAspect, initCrop, triggerUpdatePosition]);

    return (
        <JRCModal
            open={props.open}
            closeHandler={props.closeHandler}
            title={intl.formatMessage({ id: 'CROP_Title' })}
            enableClickAwayCloseModal={false}
            zIndex={props.zIndex || theme.zIndex.modal}>
            {!loaded && <JRCLoader />}
            <CropWrapper
                ref={containerRef}
                containerAspect={containerAspect}
                aspect={imageAspect}
                naturalSize={{ height: imageRef.current?.naturalHeight, width: imageRef.current?.naturalWidth }}
                isInitialAspect={isInitialAspect}>
                <ReactCrop crop={crop} onChange={setCrop} aspect={cropAspect} minWidth={10} minHeight={10}>
                    <ImageCrop rotation={rotation} scale={scale} src={props.src} ref={imageRef} />
                </ReactCrop>
            </CropWrapper>
            <ActionWrapper>
                <JRCFlexBox y="center">
                    <JRCIconButton
                        icon="icon-rotate-right"
                        type="button"
                        tooltip={{ description: 'CROP_Rotate_Right' }}
                        bgBaseGrey={1}
                        onClick={() => rotate('right')}
                    />
                    <JRCIconButton
                        icon="icon-rotate-left"
                        type="button"
                        tooltip={{ description: 'CROP_Rotate_Left' }}
                        bgBaseGrey={1}
                        onClick={() => rotate('left')}
                    />
                    {props.allowCustomRatio && (
                        <>
                            <Separator />
                            <RatioButton onClick={() => applyRatio(1)}>
                                <SquareRatioLabel>1:1</SquareRatioLabel>
                            </RatioButton>
                            <RatioButton onClick={() => applyRatio(16 / 9)}>
                                <ScreenRatioLabel>16:9</ScreenRatioLabel>
                            </RatioButton>
                            <RatioButton onClick={() => applyRatio(imageAspect)}>
                                <IconWrapper>
                                    <JRCIcon name={'icon-expand'} size={13} color={theme.color.grey4} />
                                </IconWrapper>
                            </RatioButton>
                            <Separator />
                            <RatioButton active={isLockedRatio} onClick={() => setLockedRatio(!isLockedRatio)}>
                                <IconWrapper>
                                    <JRCIcon name={'icon-lock'} size={13} color={theme.color.grey4} />
                                </IconWrapper>
                            </RatioButton>
                        </>
                    )}
                </JRCFlexBox>
                <JRCFlexBox x="flex-end">
                    <JRCButton
                        disabled={!canSave}
                        label="GLOBAL_Validate"
                        type="button"
                        icon="icon-check"
                        onClick={saveCrop}
                        loader={props.loading}
                    />
                </JRCFlexBox>
            </ActionWrapper>
        </JRCModal>
    );
}
